import './index.css';
import { config } from '../../config';
import {
  Alert,
  Flex,
  IconAdminLine,
  IconButton,
  IconCalculatorLine,
  IconCertifiedLine,
  //IconChatLine,
  IconDocumentLine,
  IconEndLine,
  IconEyeLine,
  IconInfoLine,
  IconSpLogo,
  //IconStarLightLine,
  Text,
  Tooltip,
  View,
} from '@smarterservices/smarter-ui';
import React, { useEffect, useState } from 'react';
import {
  confirmEndSession,
  dismissToolbarAlert,
  getExtensionState,
  toolbarButtonPressed,
} from '../../helpers/helpers';

const Menu = () => {
  const [toolbarButtonConfig, setToolbarButtonConfig] = useState(null);
  const [toolbarAlert, setToolbarAlert] = useState(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  useEffect(() => {
    // Listen for resizes so we can set the correct icon size
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    const initToolbar = async () => {
      const defaultToolbarConfig = {
        examPassword: false,
        attachments: true,
        sessionInfo: true,
        virtualCalculator: false,
        supportChat: true,
        proctorChat: false,
        liveView: false,
        raiseHand: false,
      };
      const config = await getExtensionState('spToolbarConfig');

      setToolbarButtonConfig(
        Object.assign(defaultToolbarConfig, config?.buttons)
      );
      setToolbarAlert(config?.currentAlert);
    };

    const handleMessage = (event) => {
      if (
        event.source === window &&
        event.data?.source === 'SmarterProctoringExtensionMessenger'
      ) {
        const payload = event.data?.payload;
        if (!payload || !payload?.message) return;

        if (payload.message === 'updateSpToolbarConfig') {
          const buttonConfig = payload.spToolbarConfig.buttons;
          const alert = payload.spToolbarConfig.currentAlert;
          setToolbarButtonConfig(buttonConfig);
          setToolbarAlert(alert);
        }
      }
    };

    initToolbar();
    window.addEventListener('message', handleMessage);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Dynamically allow the icons to change sizes based on window width
  const getIconSize = () => {
    if (windowWidth >= 1200) return 'large';
    if (windowWidth >= 950) return 'medium';
    return 'small';
  };
  const iconSize = getIconSize();

  const getButtonLeftPosition = (event) => {
    const buttonElement = event.currentTarget;
    const buttonRect = buttonElement.getBoundingClientRect();
    return buttonRect.left;
  };

  // Logic to show the most critical alert in the toolbar
  const getAlertToShow = () => {
    if (!toolbarAlert?.id) return null;

    const { level, dismissible, message } = toolbarAlert.data;
    const dismissFunction = async () => {
      await dismissToolbarAlert(toolbarAlert.id);
    };
    const closeButtonLabel = dismissible ? 'Close' : null;
    return (
      <View
        as="div"
        className="sp-alert-wrapper"
        overflowX="hidden"
        overflowY="hidden"
        position="relative"
        height="100%"
        width="100%"
      >
        <Alert
          key={toolbarAlert.id}
          margin="none"
          variant={level}
          renderCloseButtonLabel={closeButtonLabel}
          onDismiss={dismissFunction}
        >
          <div
            style={{
              overflow: 'auto',
              position: 'relative',
              height: '100%',
            }}
          >
            <Text wrap="break-word">{message}</Text>
          </div>
        </Alert>
      </View>
    );
  };
  const alertToShow = getAlertToShow();

  return (
    <View
      background="brand"
      width="100%"
      height="75px"
      overflowY="hidden"
      overflowX="hidden"
    >
      <Flex
        direction="row"
        height="100%"
        overflowY="hidden"
        overflowX="hidden"
        padding="small"
        alignItems="center"
        gap="small"
      >
        <Flex.Item margin="0 medium 0 0">
          <IconSpLogo size="medium" />
        </Flex.Item>
        {toolbarButtonConfig && (
          <>
            <Flex.Item>
              <Tooltip
                color="primary"
                renderTip={ toolbarButtonConfig?.examPassword ? "Exam Password" : "Exam Password: Unavailable" }
                placement="center end"
              >
                <IconButton
                  withBackground={false}
                  withBorder={true}
                  color="primary-inverse"
                  size={iconSize}
                  screenReaderLabel={ toolbarButtonConfig?.examPassword ? "Exam Password" : "Exam Password: Unavailable" }
                  margin="none"
                  padding="none"
                  onClick={(event) =>
                    toolbarButtonPressed('examPassword', {
                      leftPosition: getButtonLeftPosition(event),
                      sessionUiUrl: config.session.sessionUiUrl,
                      iframeId: 'spExamPasswordIframeId',
                    })
                  }
                  focusColor="inverse"
                  interaction='enabled'
                >
                  <IconAdminLine />
                </IconButton>
              </Tooltip>
            </Flex.Item>
            <Flex.Item>
              <Tooltip
                color="primary"
                renderTip={ toolbarButtonConfig?.attachments ? "Attachments" : "Attachments: Unavailable" }
                placement="center end"
              >
                <IconButton
                  withBackground={false}
                  withBorder={true}
                  color="primary-inverse"
                  size={iconSize}
                  screenReaderLabel={ toolbarButtonConfig?.attachments ? "Attachments" : "Attachments: Unavailable" }
                  margin="none"
                  padding="none"
                  onClick={(event) => {
                    toolbarButtonPressed('attachments', {
                      leftPosition: getButtonLeftPosition(event),
                      sessionUiUrl: config.session.sessionUiUrl,
                      iframeId: 'spAttachmentMenuIframeId',
                    });
                  }}
                  focusColor="inverse"
                  interaction='enabled'
                >
                  <IconDocumentLine />
                </IconButton>
              </Tooltip>
            </Flex.Item>
            <Flex.Item>
              <Tooltip
                color="primary"
                renderTip={ toolbarButtonConfig?.sessionInfo ? "Session Info" : "Session Info: Unavailable" }
                placement="center end"
              >
                <IconButton
                  withBackground={false}
                  withBorder={true}
                  color="primary-inverse"
                  size={iconSize}
                  screenReaderLabel={ toolbarButtonConfig?.sessionInfo ? "Session Info" : "Session Info: Unavailable" }
                  margin="none"
                  padding="none"
                  onClick={(event) =>
                    toolbarButtonPressed('sessionInfo', {
                      leftPosition: getButtonLeftPosition(event),
                      sessionUiUrl: config.session.sessionUiUrl,
                      iframeId: 'spSessionInfoIframeId',
                    })
                  }
                  focusColor="inverse"
                  interaction='enabled'
                >
                  <IconCertifiedLine />
                </IconButton>
              </Tooltip>
            </Flex.Item>
            <Flex.Item>
              <Tooltip
                color="primary"
                renderTip={ toolbarButtonConfig?.virtualCalculator ? "Virtual Calculator" : "Virtual Calculator: Unavailable" }
                placement="center end"
              >
                <IconButton
                  withBackground={false}
                  withBorder={true}
                  color="primary-inverse"
                  size={iconSize}
                  screenReaderLabel={ toolbarButtonConfig?.virtualCalculator ? "Virtual Calculator" : "Virtual Calculator: Unavailable" }
                  margin="none"
                  padding="none"
                  onClick={(event) =>
                    toolbarButtonPressed('virtualCalculator', {
                      leftPosition: getButtonLeftPosition(event),
                      sessionUiUrl: config.session.sessionUiUrl,
                      iframeId: 'spCalculatorIframeId',
                    })
                  }
                  focusColor="inverse"
                  interaction='enabled'
                >
                  <IconCalculatorLine />
                </IconButton>
              </Tooltip>
            </Flex.Item>
            <Flex.Item>
              <Tooltip
                color="primary"
                renderTip={ toolbarButtonConfig?.supportChat ? "Support Chat" : "Support Chat: Unavailable" }
                placement="center end"
              >
                <IconButton
                  withBackground={false}
                  withBorder={true}
                  color="primary-inverse"
                  size={iconSize}
                  screenReaderLabel={ toolbarButtonConfig?.supportChat ? "Support Chat" : "Support Chat: Unavailable" }
                  margin="none"
                  padding="none"
                  onClick={(event) =>
                    toolbarButtonPressed('supportChat', {
                      leftPosition: getButtonLeftPosition(event),
                      sessionUiUrl: config.session.sessionUiUrl,
                      iframeId: 'spSupportChatIframeId',
                    })
                  }
                  focusColor="inverse"
                  interaction='enabled'
                >
                  <IconInfoLine />
                </IconButton>
              </Tooltip>
            </Flex.Item>
            {/*}
            <Flex.Item>
              <Tooltip
                color="primary"
                renderTip={ toolbarButtonConfig?.proctorChat ? "Proctor Chat" : "Proctor Chat: Unavailable" }
                placement="center end"
              >
                <IconButton
                  withBackground={false}
                  withBorder={true}
                  color="primary-inverse"
                  size={iconSize}
                  screenReaderLabel={ toolbarButtonConfig?.proctorChat ? "Proctor Chat" : "Proctor Chat: Unavailable" }
                  margin="none"
                  padding="none"
                  onClick={(event) =>
                    toolbarButtonPressed('proctorChat', {
                      leftPosition: getButtonLeftPosition(event),
                      sessionUiUrl: config.session.sessionUiUrl,
                      iframeId: 'spProctorChatIframeId',
                    })
                  }
                  focusColor="inverse"
                  interaction='enabled'
                >
                  <IconChatLine />
                </IconButton>
              </Tooltip>
            </Flex.Item>
            */}
            <Flex.Item>
              <Tooltip
                color="primary"
                renderTip={ toolbarButtonConfig?.liveView ? "Live View" : "Live View: Unavailable" }
                placement="center end"
              >
                <IconButton
                  withBackground={false}
                  withBorder={true}
                  color="primary-inverse"
                  size={iconSize}
                  screenReaderLabel={ toolbarButtonConfig?.liveView ? "Live View" : "Live View: Unavailable" }
                  margin="none"
                  padding="none"
                  onClick={(event) =>
                    toolbarButtonPressed('liveView', {
                      leftPosition: getButtonLeftPosition(event),
                      sessionUiUrl: config.session.sessionUiUrl,
                      iframeId: 'spLiveViewIframeId',
                    })
                  }
                  focusColor="inverse"
                  interaction='enabled'
                >
                  <IconEyeLine />
                </IconButton>
              </Tooltip>
            </Flex.Item>
            {/*
            <Flex.Item>
              <Tooltip
                color="primary"
                renderTip={ toolbarButtonConfig?.raiseHand ? "Raise Hand" : "Raise Hand: Unavailable" }
                placement="center end"
              >
                <IconButton
                  withBackground={false}
                  withBorder={true}
                  color="primary-inverse"
                  size={iconSize}
                  screenReaderLabel={ toolbarButtonConfig?.raiseHand ? "Raise Hand" : "Raise Hand: Unavailable" }
                  margin="none"
                  padding="none"
                  onClick={() => toolbarButtonPressed('raiseHand')}
                  focusColor="inverse"
                  interaction='enabled'
                >
                  <IconStarLightLine />
                </IconButton>
              </Tooltip>
            </Flex.Item>
            */}
            <Flex.Item>
              <Tooltip
                color="primary"
                renderTip="End Session"
                placement="center end"
              >
                <IconButton
                  withBackground={false}
                  withBorder={true}
                  color="primary-inverse"
                  size={iconSize}
                  screenReaderLabel="End Session"
                  margin="none"
                  padding="none"
                  onClick={confirmEndSession}
                  focusColor="inverse"
                  interaction='enabled'
                >
                  <IconEndLine />
                </IconButton>
              </Tooltip>
            </Flex.Item>
            {alertToShow}
          </>
        )}
      </Flex>
    </View>
  );
};

export default Menu;
